import { Component, OnDestroy, OnInit } from '@angular/core';
import { UserPermissionKeys } from 'app/security/authorization/user-permission-keys';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LicensingService } from '../core/licensing/licensing.service';
import { PermissionsService } from '../core/permissions.service';
import { DateTimeFormatterPipe } from '../shared/pipes/date-time-formatter.pipe';
import { LicenceModuleDisplayNameProvider, LicenceModules } from '../viewmodels/LicenceModules';
import { Licence, LicenceStatus } from '../viewmodels/licence';

@Component({
  selector: 'hb-licensing-dialog',
  templateUrl: './licensing-dialog.component.html',
  styleUrls: ['./licensing-dialog.component.scss'],
})
export class LicensingDialogComponent implements OnInit, OnDestroy {
  private unsubscribe: Subject<boolean> = new Subject();
  public licence: Licence;
  public status: string;
  public canUpdate: boolean = false;
  public canUninstall: boolean = false;
  public fileExtension: string = '.dat';
  public version: string = environment.version;
  public availableModules: object[];
  public maxModelLinks: number = 0;
  public currentModelLinks: number = 0;

  public hasUpdatePermission: boolean = false;
  public hasCreatePermission: boolean = false;
  public licenseExpiryDays: number;
  public showLicenseExpiryWarning: boolean = false;

  constructor(
    private licensingService: LicensingService,
    private permissionsService: PermissionsService,
    private moduleDisplayNameProvider: LicenceModuleDisplayNameProvider,
    private dateTimeFormatterPipe: DateTimeFormatterPipe
  ) { }

  public ngOnInit() {
    this.licensingService.licence.pipe(takeUntil(this.unsubscribe)).subscribe(data => {
      this.licence = data;
      this.status = LicenceStatus[this.licence.status];
      this.canUpdate = this.licence.status !== LicenceStatus.None;
      this.canUninstall = false;
      this.maxModelLinks = this.licence.maxModelLinks;
      this.currentModelLinks = this.licence.currentModelLinks;
      this.checkLicenseExpiryDays();
      this.loadAvailableLicences();
      this.subscribeToPermissionChanges();
    });
  }

  public checkLicenseExpiryDays() {
    this.licenseExpiryDays = this.licensingService.getLicenseExpiryDays();
    this.showLicenseExpiryWarning = this.licenseExpiryDays <= 30 && this.licenseExpiryDays >= 0 && this.licence.status === LicenceStatus.Valid;
  }
  private loadAvailableLicences() {
    this.availableModules = [];
    Object.keys(LicenceModules)
      .map(key => LicenceModules[key])
      .forEach(module => {
        // eslint-disable-next-line no-bitwise
        if (this.licensingService.licence.value.licencedModules & module) {
          this.availableModules.push({
            name: this.moduleDisplayNameProvider.obtain(module),
            expiryDate: this.dateTimeFormatterPipe.transform(this.licensingService.licence.value.expiryDates[module]),
          });
        }
      });
  }

  public ngOnDestroy() {
    this.unsubscribe.next(true);
    this.unsubscribe.complete();
  }

  public install(): void {
    if (!this.hasCreatePermission) {
      return;
    }

    this.licensingService.installLicence(this.licence.customerId);
  }

  public update(): void {
    if (!(this.hasUpdatePermission && this.canUpdate)) {
      return;
    }

    this.licensingService.updateLicence();
  }

  public uninstall(): void {
    if (!(this.hasCreatePermission && this.canUninstall)) {
      return;
    }

    this.licensingService.removeLicence();
  }

  public fileChanged(event: EventTarget): void {
    if (!this.hasUpdatePermission) {
      return;
    }

    const eventObj: any = event;
    const target: HTMLInputElement = eventObj.target as HTMLInputElement;
    const files: FileList = target.files;
    const file: File = files[0];

    this.licensingService.uploadLicence(file);
  }

  private subscribeToPermissionChanges(): void {
    const permissionsKeys = new UserPermissionKeys();
    const { licenceUpdate, licenceCreate } = permissionsKeys;

    this.permissionsService.userPermissionKeyValuePairs.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.hasUpdatePermission = this.permissionsService.checkUserHasPermissions(licenceUpdate);
      this.hasCreatePermission = this.permissionsService.checkUserHasPermissions(licenceCreate);
    });
  }
}
